import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import type { Lesson } from '../types/lesson';
import { fetchLesson, fetchDateInfo, LessonRequestError } from '../services/api';
import { isLessonIdentity, lessonUrl, noteKey, parseCalendarDate, Language } from '../utils/lesson';
import { useTextArea } from '../hooks/useTextArea';
import { useNavigation } from '../hooks/useNavigation';
import { useLanguage } from '../hooks/useLanguage';
import { useDarkMode } from '../hooks/useDarkMode';
import { useFontSize } from '../hooks/useFontSize';
import { useHighlight } from '../hooks/useHighlight';
import { useLessonContext } from '../contexts/LessonContext';
import { HighlightableText } from './HighlightableText';
import { VscSettingsGear } from "react-icons/vsc";
import { FaRegCopy } from "react-icons/fa";
import { PiHighlighterLight } from "react-icons/pi";
import { CiLight } from "react-icons/ci";
import { MdOutlineDarkMode } from "react-icons/md";
import '../styles/LessonView.css';

type LessonLoadState =
  | { status: 'loading' }
  | { status: 'loaded'; lesson: Lesson }
  | { status: 'unavailable' | 'error'; message: string };

const LessonView: React.FC = () => {
  const navigate = useNavigate();
  const [showSettings, setShowSettings] = useState(false);
  const location = useLocation();
  const [loadState, setLoadState] = useState<LessonLoadState>({ status: 'loading' });
  const [attempt, setAttempt] = useState(0);
  const [showSpinner, setShowSpinner] = useState(false);
  const lesson = loadState.status === 'loaded' ? loadState.lesson : null;
  const [isCopyMode, setIsCopyMode] = useState(false);
  const [showModeNotification, setShowModeNotification] = useState(false);
  const { language: lang, year, quarter, lessonNumber, isLoading: isDateLoading, error: routeError, retry: retryDate, dateInfo, rememberDateInfo } = useLessonContext();
  const identity = { year, quarter, lessonNum: lessonNumber };
  const { autoHeight, saveNote } = useTextArea();
  const { currentSection } = useNavigation(!!lesson, location.hash);
  const { showLanguageCard, changeDefaultLanguage } = useLanguage();
  const { isDarkMode, toggleDarkMode } = useDarkMode();
  const { increaseFontSize, decreaseFontSize, resetFontSize } = useFontSize();
  const { addHighlight, addMultiParagraphHighlight, removeHighlight, updateHighlightColor, getHighlightsForSection } = useHighlight();

  useEffect(() => {
    let active = true;
    const controller = new AbortController();
    const loadLesson = async () => {
      if (isDateLoading || !isLessonIdentity({ year, quarter, lessonNum: lessonNumber })) return;
      try {
        const data = await fetchLesson(lang, year, quarter, lessonNumber, controller.signal);
        if (active) setLoadState({ status: 'loaded', lesson: data });
      } catch (loadError) {
        if (!active) return;
        const unavailable = loadError instanceof LessonRequestError && loadError.kind === 'unavailable';
        setLoadState({
          status: unavailable ? 'unavailable' : 'error',
          message: unavailable ? `This lesson is currently not available in ${getLanguageName(lang)}` :
            loadError instanceof LessonRequestError ? loadError.message : 'Unable to load this lesson. Please try again.'
        });
      }
    };
    loadLesson();
    return () => { active = false; controller.abort(); };
  }, [lang, year, quarter, lessonNumber, isDateLoading, attempt]);

  useEffect(() => {
    if (!lesson || dateInfo || lesson.isOffline) return;
    let active = true;
    const controller = new AbortController();
    // Direct lesson URLs need their own navigation metadata; current-entry redirects already have it.
    const lessonDate = parseCalendarDate(lesson.Date);
    const dailyDate = lesson.DayLessons.map(day => ({ day, date: parseCalendarDate(day.Date) }))
      .find(item => item.date !== null);
    const targetDate = lessonDate ?? dailyDate?.date;
    if (!targetDate) return;
    const sabbath = new Date(targetDate);
    if (!lessonDate && dailyDate) sabbath.setDate(sabbath.getDate() + (7 - dailyDate.day.Number));
    fetchDateInfo(sabbath.getFullYear(), sabbath.getMonth() + 1, sabbath.getDate(), controller.signal).then(data => {
      if (active && data.year === year && data.quarter === quarter && data.lessonNum === lessonNumber) rememberDateInfo(data);
    }).catch(dateError => {
      if (active) console.error('Error fetching navigation dates:', dateError);
    });
    return () => { active = false; controller.abort(); };
  }, [lesson, dateInfo, year, quarter, lessonNumber, rememberDateInfo]);

  useEffect(() => {
    if (loadState.status !== 'loading' || routeError) { setShowSpinner(false); return; }
    const timer = setTimeout(() => setShowSpinner(true), 150);
    return () => clearTimeout(timer);
  }, [loadState.status, routeError]);

  const retryLesson = () => {
    setLoadState({ status: 'loading' });
    setAttempt(value => value + 1);
  };
  const previousLesson = dateInfo && isLessonIdentity(dateInfo.prev) ? dateInfo.prev : undefined;
  const nextLesson = dateInfo && isLessonIdentity(dateInfo.next) ? dateInfo.next : undefined;

  useEffect(() => {
    if (lesson) document.querySelectorAll('textarea').forEach(autoHeight);
  }, [lesson, autoHeight]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const settingsPanel = document.querySelector('.settings-panel');
      if (settingsPanel && !settingsPanel.contains(e.target as Node)) {
        setShowSettings(false);
      }
    };

    const handleScroll = () => {
      setShowSettings(false);
    };

    if (showSettings) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('scroll', handleScroll);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('scroll', handleScroll);
    };
  }, [showSettings]);

  // Global selection handler for multi-paragraph highlights
  useEffect(() => {
    if (isCopyMode) return; // Don't handle highlights in copy mode

    const handleGlobalSelection = () => {
      const selection = window.getSelection();
      if (!selection || selection.isCollapsed || selection.rangeCount === 0) return;

      const range = selection.getRangeAt(0);

      // Find all paragraph elements that intersect with the selection
      const allParagraphs = document.querySelectorAll('.bible-text, .sop-text');
      const affectedParagraphs: Array<{
        element: Element;
        type: 'sop' | 'bible';
        dayNumber: number;
        questionIndex: number;
        index: number;
        startOffset: number;
        endOffset: number;
        text: string;
      }> = [];

      allParagraphs.forEach((para) => {
        // Check if this paragraph intersects with the selection
        if (!range.intersectsNode(para)) return;

        // Get metadata from the element's attributes or parent
        const type = para.classList.contains('sop-text') ? 'sop' : 'bible';

        // For bible, use the section container; for sop, use the paragraph itself
        // This matches the logic in HighlightableText component
        const container = type === 'bible' ? para.closest('section.bible-verses') : para;
        if (!container) return;

        // Extract metadata from the component structure
        const daySection = para.closest('section[id]');
        if (!daySection) return;
        const dayNumber = parseInt(daySection.id);

        // Find the question index and paragraph index
        // The question div is the one that contains an h4 with class "question-text"
        // Walk up from the paragraph to find the question div
        let questionDiv: HTMLElement | null = para.closest('div');
        if (!questionDiv) return;

        // Walk up to find the div that contains the question (has an h4 with question-text class)
        while (questionDiv && !questionDiv.querySelector('h4 .question-text')) {
          const parent: HTMLElement | null = questionDiv.parentElement;
          if (!parent || parent === daySection || parent.tagName !== 'DIV') break;
          questionDiv = parent;
        }

        // If we still don't have a question div, try finding it differently
        if (!questionDiv || !questionDiv.querySelector('h4 .question-text')) {
          // Fallback: find the closest div that's a direct child of daySection
          questionDiv = para.closest('div');
          while (questionDiv && questionDiv.parentElement !== daySection) {
            questionDiv = questionDiv.parentElement?.closest('div') || null;
          }
        }

        if (!questionDiv) return;

        // Get all question divs in this day (direct children divs that contain h4.question-text)
        // Exclude divs like lineBreak and heading
        const allQuestions = Array.from(daySection.children).filter((child): child is HTMLElement => {
          if (child.tagName !== 'DIV') return false;
          return !!(child as HTMLElement).querySelector('h4 .question-text');
        }) as HTMLElement[];

        const questionIndex = allQuestions.indexOf(questionDiv);
        if (questionIndex === -1) return;

        // Get the paragraph index within this question
        const allParasInQuestion = questionDiv.querySelectorAll(type === 'sop' ? '.sop-text' : '.bible-verses');
        const index = Array.from(allParasInQuestion).indexOf(container);
        if (index === -1) return;

        // Calculate offsets within the container
        const fullText = container.textContent || '';

        // Determine if this container contains the start or end of the selection
        const containsStart = container.contains(range.startContainer) || container === range.startContainer;
        const containsEnd = container.contains(range.endContainer) || container === range.endContainer;

        let startOffset = 0;
        let endOffset = fullText.length;

        // If selection starts in this container, calculate the start offset
        if (containsStart) {
          const beforeStart = document.createRange();
          beforeStart.setStart(container, 0);
          beforeStart.setEnd(range.startContainer, range.startOffset);
          startOffset = beforeStart.toString().length;
        }

        // If selection ends in this container, calculate the end offset
        if (containsEnd) {
          const beforeEnd = document.createRange();
          beforeEnd.setStart(container, 0);
          beforeEnd.setEnd(range.endContainer, range.endOffset);
          endOffset = beforeEnd.toString().length;
        }

        // For paragraphs that are between the start and end (but don't contain either),
        // highlight the entire paragraph
        // This ensures that if selection spans paragraph 1 -> paragraph 3,
        // paragraph 2 will also be fully highlighted
        if (!containsStart && !containsEnd) {
          // This paragraph is fully within the selection
          startOffset = 0;
          endOffset = fullText.length;
        }

        // Only add if there's actual text selected in this paragraph
        // Check that we have a valid range and non-empty text
        const selectedText = fullText.slice(startOffset, endOffset);
        if (startOffset < endOffset && selectedText.trim().length > 0) {
          affectedParagraphs.push({
            element: para,
            type,
            dayNumber,
            questionIndex,
            index,
            startOffset,
            endOffset,
            text: fullText
          });
        }
      });

      // If multiple paragraphs are affected, use multi-paragraph highlight
      if (affectedParagraphs.length > 1) {
        addMultiParagraphHighlight(
          affectedParagraphs.map(p => ({
            type: p.type,
            dayNumber: p.dayNumber,
            questionIndex: p.questionIndex,
            sopIndex: p.type === 'sop' ? p.index : undefined,
            verseIndex: p.type === 'bible' ? p.index : undefined,
            startOffset: p.startOffset,
            endOffset: p.endOffset,
            text: p.text
          }))
        );
        selection.removeAllRanges();
      }
      // Single paragraph selections are handled by HighlightableText component
    };

    document.addEventListener('mouseup', handleGlobalSelection);
    document.addEventListener('touchend', handleGlobalSelection);

    return () => {
      document.removeEventListener('mouseup', handleGlobalSelection);
      document.removeEventListener('touchend', handleGlobalSelection);
    };
  }, [isCopyMode, addMultiParagraphHighlight]);

  const handleTextAreaChange = (e: React.FormEvent<HTMLTextAreaElement>) => {
    const element = e.target as HTMLTextAreaElement;
    saveNote(element);
  };

  // const handleTextAreaFocus = (e: React.FocusEvent<HTMLTextAreaElement>) => {
  //   const element = e.target as HTMLTextAreaElement;
  //   autoHeight(element);
  // };

  // const handleTextAreaBlur = (e: React.FocusEvent<HTMLTextAreaElement>) => {
  //   const element = e.target as HTMLTextAreaElement;
  //   autoHeight(element);
  // };

  const toggleCopyMode = () => {
    setIsCopyMode(prev => {
      const newMode = !prev;
      // Show notification when switching modes
      setShowModeNotification(true);
      setTimeout(() => setShowModeNotification(false), 2000);
      return newMode;
    });
  };

  const getDayOfWeek = (dayNumber: number, language: string) => {
    const dayNames: Record<string, string[]> = {
      'en': ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
      'pt': ['Domingo', 'Segunda-feira', 'Terça-feira', 'Quarta-feira', 'Quinta-feira', 'Sexta-feira', 'Sábado'],
      'ro': ['Duminică', 'Luni', 'Marți', 'Miercuri', 'Joi', 'Vineri', 'Sâmbătă'],
      'sp': ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'],
      'de': ['Sonntag', 'Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag'],
      'fr': ['Dimanche', 'Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi'],
      'hr': ['Nedjelja', 'Ponedjeljak', 'Utorak', 'Srijeda', 'Četvrtak', 'Petak', 'Subota']
    };

    const days = dayNames[language] || dayNames['en'];
    return days[dayNumber - 1] || '';
  };

  const getLanguageName = (languageCode: string): string => {
    const languageNames: Record<string, string> = {
      'en': 'English',
      'pt': 'Portuguese',
      'ro': 'Romanian',
      'sp': 'Spanish',
      'de': 'German',
      'fr': 'French',
      'hr': 'Croatian'
    };

    return languageNames[languageCode] || 'English';
  };

  const getDayInitial = (dayNumber: number, language: string) => {
    const dayName = getDayOfWeek(dayNumber, language);
    return dayName.charAt(0).toUpperCase();
  };

  const stripHtmlTags = (text: string): string => {
    if (!text) return '';
    // Use regex to remove HTML tags while preserving text content
    return text.replace(/<[^>]*>/g, '');
  };

  const languageHref = (language: Language) => isLessonIdentity(identity)
    ? lessonUrl(language, identity, window.location.hash)
    : `/ssl/${language}${window.location.hash}`;
  const languages: { code: Language; name: string; icon: string }[] = [
    { code: 'en', name: 'English', icon: 'english' },
    { code: 'pt', name: 'Portuguese', icon: 'portuguese' },
    { code: 'ro', name: 'Romanian', icon: 'romanian' },
    { code: 'sp', name: 'Spanish', icon: 'spanish' },
    { code: 'de', name: 'German', icon: 'de-german' },
    { code: 'fr', name: 'French', icon: 'fr-french' },
    { code: 'hr', name: 'Croatian', icon: 'hr-croatian' }
  ];
  const renderLanguageNavigation = () => (
    <div className="heading-navigation-wrapper">
      <div className="language-links-wrapper">
        {languages.map(language => (
          <Link key={language.code} to={languageHref(language.code)}
            onClick={e => {
              changeDefaultLanguage(language.code);
              // Read the live fragment, including scroll changes since the last render.
              const destination = languageHref(language.code);
              e.currentTarget.href = destination;
              if (!e.defaultPrevented && e.button === 0 && !e.metaKey && !e.ctrlKey && !e.shiftKey && !e.altKey) {
                e.preventDefault();
                navigate(destination);
              }
            }}
            className={lang === language.code ? 'selected' : ''}>
            <img src={`/images/icons/${language.icon}.svg`} alt={language.name} />
          </Link>
        ))}
      </div>
      <div className="navigation-buttons">
        {previousLesson && (
          <Link to={lessonUrl(lang, previousLesson)}>
            <h3 className="link small">previous lesson</h3>
          </Link>
        )}
        {nextLesson && (
          <Link to={lessonUrl(lang, nextLesson)}>
            <h3 className="link small">next lesson</h3>
          </Link>
        )}
      </div>
    </div>
  );

  // Add language selection card content
  const renderLanguageCard = () => (
    <div className="selectDefaultLanguageWrapper" id="selectDefaultLanguage">
      <div className="cardMain">
        <img className="icon-gif" src="/images/earth_spin_256.gif" alt="Earth spinning" />
        <div className="cardTitle">Select Default Language</div>
        <div className="cardDescription">you can change it later at the top and bottom of every lesson</div>
        <div className="language-select">
          {languages.map(language => (
            <button key={language.code} onClick={() => {
              changeDefaultLanguage(language.code);
              navigate(languageHref(language.code));
            }} className="language-button">
              <img src={`/images/icons/${language.icon}.svg`} alt={language.name} />
              <span>{language.name}</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );

  if (!lesson) {
    return (
      <>
        {renderLanguageNavigation()}
        <div className="loading" role="status" aria-live="polite" aria-busy={!routeError && loadState.status === 'loading'}>
          {routeError ? (
            <><p>{routeError}</p>{retryDate && <button onClick={retryDate}>Try again</button>}</>
          ) : loadState.status === 'error' || loadState.status === 'unavailable' ? (
            <><p>{loadState.message}</p><button onClick={retryLesson}>Try again</button></>
          ) : (
            <>
              <div className={showSpinner ? 'loading-spinner' : 'loading-spinner-placeholder'} aria-hidden="true" />
              <p>Loading lesson...</p>
            </>
          )}
        </div>
      </>
    );
  }

  // Show offline message if the lesson is in offline mode
  const offlineBanner = lesson.isOffline && (
    <div className="offline-banner">
      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <line x1="1" y1="1" x2="23" y2="23"></line>
        <path d="M16.72 11.06A10.94 10.94 0 0 1 19 12.55"></path>
        <path d="M5 12.55a10.94 10.94 0 0 1 5.17-2.39"></path>
        <path d="M10.71 5.05A16 16 0 0 1 22.58 9"></path>
        <path d="M1.42 9a15.91 15.91 0 0 1 4.7-2.88"></path>
        <path d="M8.53 16.11a6 6 0 0 1 6.95 0"></path>
        <line x1="12" y1="20" x2="12.01" y2="20"></line>
      </svg>
      <span>You're offline. Some content may not be available.</span>
    </div>
  );

  // Show mode switching notification
  const modeNotification = showModeNotification && (
    <div className="copy-notification">
      Switched to {isCopyMode ? 'copy' : 'highlight'} mode
    </div>
  );

  return (
    <>
      {renderLanguageNavigation()}
      {offlineBanner}
      {modeNotification}
      {showLanguageCard && renderLanguageCard()}
      <div className="day-select-navigation-bar">
        <div className="navigation-wrapper">
          <div className="day-wrapper">
            {[7, 1, 2, 3, 4, 5, 6, '★'].map((day, index) => {
              const sectionId = index === 0 ? 'heading' : index === 1 ? '1' : index === 7 ? '7' : day.toString();
              const dayText = day === '★' ? '★' : getDayInitial(day as number, lang || 'en');
              return (
                <a key={index} href={`#${sectionId}`} className="dayLink">
                  <div className={`day ${currentSection === sectionId ? 'active' : ''}`}>{dayText}</div>
                </a>
              );
            })}
          </div>
        </div>
      </div>

      <div className={`main-page-wrapper ${isCopyMode ? 'copy-mode' : ''}`}>
        <section className="lesson-section" id="heading">
          <div className="heading">
            <h3>Lesson {lesson.Number}</h3>
          </div>
          <h1 className="bold center">{stripHtmlTags(lesson.Title)}</h1>
          <p className="bold">{stripHtmlTags(lesson.KeyText)}</p>
          <section className="question-comment-textarea-wrapper">
            <textarea
              key={noteKey(lang, identity, 'heading')}
              name={noteKey(lang, identity, 'heading')}
              placeholder="your comments..."
              rows={1}
              onInput={handleTextAreaChange}
              onFocus={e => autoHeight(e.currentTarget)}
              onBlur={e => autoHeight(e.currentTarget)}
              onChange={handleTextAreaChange}
              defaultValue={localStorage.getItem(noteKey(lang, identity, 'heading')) || ''}
            />
          </section>
          <p>{stripHtmlTags(lesson.KeyNote)}</p>
          {lesson.SuggestedReading && (
            <p className="left">
              {/* <a className="flex-col" href="#60"> */}
                <b className="bold">Suggested Reading: </b> {stripHtmlTags(lesson.SuggestedReading.Text || '')}
              {/* </a> */}
            </p>
          )}
        </section>

        {lesson.DayLessons.map((day) => (
          <section key={day.Number} className="lesson-section" id={day.Number.toString()}>
            <div className="lineBreak" />
            <div className="heading">
              <h3 className="day-of-week">{getDayOfWeek(day.Number, lang || 'en')}</h3>
              <h3 className="day-date">{day.Date}</h3>
            </div>
            <h2>{day.Subtitle}</h2>

            {day.Questions.map((question, qIndex) => (
              <div key={qIndex}>
                <h4><span className="question-text">{stripHtmlTags(question.Text)}</span></h4>
                {question.VerseRefs?.map((verseRef, vIndex) => (
                  <section key={vIndex} className="bible-verses">
                    <HighlightableText
                      type="bible"
                      text={verseRef.Verse.map((verse, i) =>
                        i === 0
                          ? `${verse.Book} ${verse.Chapter}:${verse.Verse} ${stripHtmlTags(verse.Text)}`
                          : `${verse.Verse} ${stripHtmlTags(verse.Text)}`
                      ).join(' ')}
                      dayNumber={day.Number}
                      questionIndex={qIndex}
                      index={vIndex}
                      onHighlight={isCopyMode ? () => {} : addHighlight}
                      onRemoveHighlight={isCopyMode ? () => {} : removeHighlight}
                      onUpdateHighlightColor={isCopyMode ? () => {} : updateHighlightColor}
                      highlights={isCopyMode ? [] : getHighlightsForSection('bible', day.Number, qIndex, vIndex)}
                      disableHighlighting={isCopyMode}
                    />
                  </section>
                ))}
                <section className="question-comment-textarea-wrapper">
                  <textarea
                    key={noteKey(lang, identity, day.Number, qIndex)}
                    name={noteKey(lang, identity, day.Number, qIndex)}
                    placeholder="your comments..."
                    rows={1}
                    onInput={handleTextAreaChange}
                    onFocus={e => autoHeight(e.currentTarget)}
                    onBlur={e => autoHeight(e.currentTarget)}
                    onChange={handleTextAreaChange}
                    defaultValue={localStorage.getItem(noteKey(lang, identity, day.Number, qIndex)) || ''}
                  />
                </section>
                {question.SopText?.map((text, sopIndex) => (
                  <HighlightableText
                    key={sopIndex}
                    type="sop"
                    text={stripHtmlTags(text)}
                    dayNumber={day.Number}
                    questionIndex={qIndex}
                    index={sopIndex}
                    onHighlight={isCopyMode ? () => {} : addHighlight}
                    onRemoveHighlight={isCopyMode ? () => {} : removeHighlight}
                    onUpdateHighlightColor={isCopyMode ? () => {} : updateHighlightColor}
                    highlights={isCopyMode ? [] : getHighlightsForSection('sop', day.Number, qIndex, sopIndex)}
                    disableHighlighting={isCopyMode}
                  />
                ))}
              </div>
            ))}
          </section>
        ))}
      </div>

      <div className="settings-panel">
        <div className="mode-toggle-wrapper">
          <button
            className={`settings-button mode-icon ${isCopyMode ? 'active' : ''}`}
            onClick={toggleCopyMode}
            aria-label={isCopyMode ? "Switch to highlight mode" : "Switch to copy mode"}
          >
            {isCopyMode ? (
              // Copy mode - show highlight icon
              <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                {PiHighlighterLight({ size: 24 })}
              </span>
            ) : (
              // Highlight mode - show copy icon
              <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                {FaRegCopy({ size: 24 })}
              </span>
            )}
          </button>
        </div>
        {!showSettings && (
          <button
            className="settings-button"
            onClick={() => setShowSettings(true)}
            aria-label="Toggle settings panel"
          >
            <span style={{ display: 'inline-flex', alignItems: 'center' }}>
              {VscSettingsGear({ size: 24 })}
            </span>
          </button>
        )}

        {showSettings && (
          <>
            <div className="font-size-controls">
              <button
                className="settings-button"
                onClick={decreaseFontSize}
                aria-label="Decrease font size"
              >
                A-
              </button>
              <button
                className="settings-button"
                onClick={increaseFontSize}
                aria-label="Increase font size"
              >
                A+
              </button>
              <button
                className="settings-button"
                onClick={resetFontSize}
                aria-label="Reset font size"
              >
                ↺
              </button>
            </div>
            <button
              className="settings-button"
              onClick={toggleDarkMode}
              aria-label={isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'}
            >
              <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                {isDarkMode ? CiLight({ size: 24 }) : MdOutlineDarkMode({ size: 24 })}
              </span>
            </button>
          </>
        )}
      </div>

      {/* <footer className="lesson-section" id="7">

        <div className="settings">
          <a href="/auto" onClick={clearDefaultLanguage} className="clear-language-link">clear default language</a>
        </div>
      </footer> */}
    </>
  );
};

// Remount lesson state and uncontrolled note fields together when the route changes.
const RoutedLessonView: React.FC = () => {
  const { language, year, quarter, lessonNumber } = useLessonContext();
  return <LessonView key={`${language}-${year}-${quarter}-${lessonNumber}`} />;
};
export default RoutedLessonView;
